import { FunctionComponent } from 'react';
import styles from '../styles.module.scss';
import { CourseDurationLabel } from './components/CourseDurationLabel';
import { ClockRegularIcon } from '../../Icons/ClockRegularIcon';
import { PublicCourse, PublicOccurrence } from '@cademy-package/sdk-component-types';

type CourseDurationHighlightProps = {
    course: PublicCourse;
    occurrences: PublicOccurrence[] | null;
};

const isChildOccurrence = (occurrence: PublicOccurrence): boolean => {
    return Boolean('parent_ref' in occurrence && occurrence.parent_ref);
};

export const CourseDurationHighlight: FunctionComponent<CourseDurationHighlightProps> = ({
    course,
    occurrences,
}) => {
    if (course.type === 'on-request') {
        return null;
    }

    if (['on-demand', 'blended'].includes(course.type) && course.course_duration) {
        return (
            <li className={styles.highlight} aria-label="course duration">
                <span className={styles.icon}>
                    <ClockRegularIcon />
                </span>
                <CourseDurationLabel
                    courseDuration={course.course_duration.duration * 60}
                    isOnDemand={course.type === 'on-demand'}
                />
            </li>
        );
    }

    if (occurrences && occurrences.length > 0) {
        const nonChildOccurrences = occurrences.filter(
            (occurrence) => !isChildOccurrence(occurrence)
        );

        if (nonChildOccurrences.length === 0) {
            return null;
        }

        const durations = nonChildOccurrences.map(
            (occurrence) => occurrence.end_datetime - occurrence.start_datetime
        );

        const uniqueDurations = [...new Set(durations)];
        const hasMultipleDurations = uniqueDurations.length > 1;

        return (
            <li className={styles.highlight} aria-label="course duration">
                <span className={styles.icon}>
                    <ClockRegularIcon />
                </span>
                {hasMultipleDurations ? (
                    <span>Multiple Durations (see dates)</span>
                ) : (
                    <CourseDurationLabel
                        courseDuration={durations[0]}
                        isOnDemand={course.type === 'on-demand'}
                    />
                )}
            </li>
        );
    }

    return null;
};
